import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { List, Map as ImmutableMap } from 'immutable';
import hubHttpClient from 'hub-http/clients/apiClient';
import { callIfPossible } from 'UIComponents/core/Functions';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import DocumentsPopoverForm from './DocumentsPopoverForm';
import { searchDocuments, searchDocumentsCHIRP } from './DocumentsApi';
import FormattedMessage from 'I18n/components/FormattedMessage';
import DocumentsPopoverEmptyState from './DocumentsPopoverEmptyState';
import { fetchDocuments, fetchDocumentsCHIRP } from './fetchDocuments';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import { renderDocument, renderDocumentCHIRP } from './renderDocuments';
import Raven from 'raven-js';
import { isUngatedForDocumentsCHIRP } from './isUngatedForDocumentsCHIRP';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import I18n from 'I18n';
import { jsx as _jsx } from "react/jsx-runtime";
function getDocumentFetchFn(customFn) {
  if (customFn) return customFn;
  const fn = isUngatedForDocumentsCHIRP() ? fetchDocumentsCHIRP : fetchDocuments;
  return () => fn({
    httpClient: hubHttpClient
  });
}
function getDocumentSearchFn(customFn) {
  if (customFn) return customFn;
  return isUngatedForDocumentsCHIRP() ? searchDocumentsCHIRP : searchDocuments;
}
function _buildSelectData(documents) {
  return Object.values(documents).reduce((documentList, document) => {
    return documentList.push(ImmutableMap({
      value: `${document.contentId}`,
      text: document.name
    }));
  }, List()).sortBy(document => document.get('name')).toJS();
}
const DocumentsPopover = ({
  contactEmail,
  outputType = contentOutputs.RENDERED,
  onChange,
  onClose,
  onFetchDocumentsError,
  showIncludeLinkPreview = true,
  showRequireEmail = true,
  onToggleAllowDownload,
  fetchDocumentsFn,
  searchDocumentsFn,
  renderDocumentFn
}) => {
  const [hasFetchedDocuments, setHasFetchedDocuments] = useState(false);
  const [documentsSearchResults, setDocumentsSearchResults] = useState({});
  const [totalDocuments, setTotalDocuments] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [documentName, setDocumentName] = useState(null);
  const [linkText, setLinkText] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [requireEmail, setRequireEmail] = useState(true);
  const [consentRequiredToView, setConsentRequiredToView] = useState(false);
  const [includeLinkPreview, setIncludeLinkPreview] = useState(false);
  const [allowDownload, setAllowDownload] = useState(true);
  const [linkInfo, setLinkInfo] = useState(null);
  const [isRendering, setIsRendering] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(undefined);
  const effectiveRequireEmail = requireEmail || consentRequiredToView;
  const handleDocumentSelect = useCallback(e => {
    const newId = parseInt(e.target.value, 10);
    if (newId !== documentId) {
      var _newDocument$brandId;
      const oldDocument = documentId ? documentsSearchResults[documentId] : undefined;
      const newDocument = documentsSearchResults[newId];
      const textIsTitle = oldDocument !== undefined && linkText === oldDocument.metadata.title;
      if (linkText === null || textIsTitle) {
        setLinkText(newDocument.metadata.title);
      }
      setDocumentId(newId);
      setDocumentName(newDocument.metadata.title);
      setPreviewTitle(newDocument.metadata.title);
      setPreviewImage(newDocument.metadata.thumbnailUrl);
      setSelectedBrandId((_newDocument$brandId = newDocument.brandId) !== null && _newDocument$brandId !== void 0 ? _newDocument$brandId : 0);
      setConsentRequiredToView(Boolean(newDocument.legalConsentEnabled));
    }
  }, [documentId, linkText, documentsSearchResults]);
  const handleTextChange = useCallback(({
    target
  }) => {
    const {
      value
    } = target;
    if (linkText !== value && value.length < 300) {
      setLinkText(value);
    }
  }, [linkText]);
  const toggleRequireEmail = useCallback(() => {
    setRequireEmail(oldState => !oldState);
  }, []);
  const toggleLinkPreview = useCallback(() => {
    setIncludeLinkPreview(oldState => !oldState);
  }, []);
  const toggleAllowDownload = useCallback(() => {
    setAllowDownload(oldState => {
      const newValue = !oldState;
      onToggleAllowDownload === null || onToggleAllowDownload === void 0 || onToggleAllowDownload(newValue);
      return newValue;
    });
  }, [onToggleAllowDownload]);
  const handleClose = useCallback(() => {
    callIfPossible(onClose);
  }, [onClose]);
  const buildSelectData = useCallback(() => {
    if (totalDocuments === 0) {
      return [];
    }
    return _buildSelectData(documentsSearchResults);
  }, [documentsSearchResults, totalDocuments]);
  const renderDisplayText = useMemo(() => {
    return /*#__PURE__*/_jsx(FormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.documents.decksModal.linkTextLabel"
      }),
      children: /*#__PURE__*/_jsx(TextInput, {
        "data-test-id": "display-text",
        onChange: handleTextChange,
        value: linkText
      })
    });
  }, [handleTextChange, linkText]);
  const handleConfirm = useCallback(async (closePopover = true) => {
    let resolvedLinkInfo = linkInfo;
    if (renderDocumentFn && documentId && !resolvedLinkInfo) {
      setIsRendering(true);
      try {
        resolvedLinkInfo = await renderDocumentFn({
          documentId,
          documentName: documentName !== null && documentName !== void 0 ? documentName : '',
          allowDownload
        });
      } catch (error) {
        Raven.captureException(error);
        setIsRendering(false);
        FloatingAlertStore.addAlert({
          type: 'danger',
          titleText: I18n.text('hubspot-prosemirror.documents.decksModal.renderError')
        });
        return;
      }
      setIsRendering(false);
    }
    onChange({
      documentId: documentId,
      documentName: documentName,
      displayText: linkText,
      requireEmail: effectiveRequireEmail,
      includeLinkPreview,
      previewTitle,
      previewImage,
      linkInfo: resolvedLinkInfo,
      allowDownload
    });
    if (closePopover) {
      handleClose();
    }
  }, [handleClose, onChange, documentId, documentName, linkText, effectiveRequireEmail, includeLinkPreview, previewTitle, previewImage, linkInfo, allowDownload, renderDocumentFn]);
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    getDocumentFetchFn(fetchDocumentsFn)().then(({
      results,
      total
    }) => {
      const searchResults = results.reduce((acc, result) => {
        acc[result.contentId] = result;
        return acc;
      }, {});
      setDocumentsSearchResults(searchResults);
      setTotalDocuments(total);
      setHasFetchedDocuments(true);
    }).catch(err => {
      setHasFetchedDocuments(true);
      callIfPossible(onFetchDocumentsError, err);
    });
  }, [onFetchDocumentsError, fetchDocumentsFn]);
  useEffect(() => {
    if (!documentId) return;
    if (!(outputType === contentOutputs.RENDERED && contactEmail)) return;
    if (renderDocumentFn) return;
    const promise = isUngatedForDocumentsCHIRP() ? renderDocumentCHIRP({
      httpClient: hubHttpClient,
      contactEmail: contactEmail,
      contentId: documentId,
      documentName: documentName !== null && documentName !== void 0 ? documentName : '',
      requireEmail: effectiveRequireEmail,
      allowDownload
    }) : renderDocument({
      httpClient: hubHttpClient,
      contactEmail: contactEmail,
      contentId: documentId,
      requireEmail: effectiveRequireEmail,
      allowDownload
    });
    promise.then(renderedLink => {
      setLinkInfo(renderedLink);
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [contactEmail, outputType, documentId, documentName, effectiveRequireEmail, allowDownload, renderDocumentFn]);
  if (!hasFetchedDocuments) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        width: '400px'
      },
      "data-test-id": "documents-popover",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true,
        minHeight: 350
      })
    });
  }
  if (hasFetchedDocuments && totalDocuments === 0) {
    return /*#__PURE__*/_jsx("div", {
      style: {
        width: '400px'
      },
      "data-test-id": "documents-popover",
      children: /*#__PURE__*/_jsx(DocumentsPopoverEmptyState, {})
    });
  }
  const documentOptions = buildSelectData();
  return /*#__PURE__*/_jsx(DocumentsPopoverForm, {
    selectedDocumentId: documentId,
    selectedBrandId: selectedBrandId,
    requireEmail: effectiveRequireEmail,
    consentRequiredToView: consentRequiredToView,
    documentOptions: documentOptions,
    displayText: renderDisplayText,
    includeLinkPreview: includeLinkPreview,
    toggleLinkPreview: toggleLinkPreview,
    allowDownload: allowDownload,
    toggleAllowDownload: toggleAllowDownload,
    handleDocumentSelect: handleDocumentSelect,
    setDocumentsSearchResults: setDocumentsSearchResults,
    searchDocumentsFn: getDocumentSearchFn(searchDocumentsFn),
    toggleRequireEmail: toggleRequireEmail,
    handleConfirm: handleConfirm,
    handleCancel: handleClose,
    showIncludeLinkPreview: showIncludeLinkPreview,
    showRequireEmail: showRequireEmail,
    isConfirmDisabled: isRendering
  });
};
export default DocumentsPopover;